import type { DTO } from '@/type-utils';
import { removeUndefined } from '@/utils/object-utils';
import Model from '../Model';
import type { INavigation } from './INavigation';
import { createCategoryPathname } from './utils';

/**
 * A model that represents a navigation item.
 */
export class NavigationModel
  extends Model<DTO<INavigation>>
  implements INavigation
{
  /** @inheritdoc */
  public readonly id: string;

  /** @inheritdoc */
  public readonly name: string;

  /** @inheritdoc */
  public readonly onlineSubCategoriesCount?: number;

  /** @inheritdoc */
  public readonly categories?: ReadonlyArray<NavigationModel>;

  /** @inheritdoc */
  private readonly _href?: string;

  /** @inheritdoc */
  public get href(): string {
    return this._href ?? createCategoryPathname(this.id);
  }

  /**
   * Creates a new instance of the {@link NavigationModel}.
   *
   * @param dto - An INavigation DTO.
   */
  public constructor(dto: DTO<INavigation>) {
    super(dto);

    this.id = dto.id;
    this.name = dto.name;
    this.onlineSubCategoriesCount = dto.onlineSubCategoriesCount;
    this.categories = dto.categories?.map((category: DTO<INavigation>) =>
      NavigationModel.from(category)
    );
    this._href = dto.href;
  }

  /**
   * Converts the model to a DTO.
   *
   * @returns - An INavigation DTO.
   */
  public override toDTO(): DTO<INavigation> {
    return removeUndefined({
      id: this.id,
      name: this.name,
      onlineSubCategoriesCount: this.onlineSubCategoriesCount,
      categories: this.categories?.map((category: INavigation) =>
        NavigationModel.from(category).toDTO()
      )
    }) as DTO<INavigation>;
  }
}
